import React from 'react';
import { PaymentMethod, MovementCategory, UserRole, UserType } from '../types/index';
import { pagamentosDoMovimento } from '../shared/logica/pagamentos';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { formatPtMonthYear } from '../utils/dateI18n';
import { ChevronLeft, ChevronRight, TrendingUp, DollarSign, CreditCard, Clock, PieChart, BarChart } from 'lucide-react';
import { BarChart as RechartsBar, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart as RechartsPie, Pie } from 'recharts';

export default function ReportsView({ movements, currentUser }) {
  const [selectedMonth, setSelectedMonth] = React.useState(new Date());

  const start = startOfMonth(selectedMonth);
  const end = endOfMonth(selectedMonth);

  // Calcular estatísticas considerando pagamentos mistos com datas diferentes
  const stats = React.useMemo(() => {
    const isDateInMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= start && d <= end;
    };
    let total = 0;
    let cash = 0;
    let card = 0;
    let pending = 0;

    movements.forEach(m => {
      if (m.category !== MovementCategory.OPERATIONAL) return;

      // Para pagamentos mistos, verificar cada parte do pagamento separadamente
      if (m.isMixedPayment) {
        pagamentosDoMovimento(m).forEach(p => {
          const paymentDate = (p.paymentDate || m.date || '').split('T')[0];
          if (!isDateInMonth(paymentDate)) return;
          total += p.value;
          if (p.method === PaymentMethod.CASH) cash += p.value;
          if (p.method === PaymentMethod.CARD) card += p.value;
          if (p.method === PaymentMethod.PENDING) pending += p.value;
        });
      } else {
        // Para pagamentos simples, verificar a data principal
        if (isDateInMonth(m.date)) {
          const val = Number(m.value || 0);
          total += val;
          if (m.paymentMethod === PaymentMethod.CASH) cash += val;
          if (m.paymentMethod === PaymentMethod.CARD) card += val;
          if (m.paymentMethod === PaymentMethod.PENDING) pending += val;
        }
      }
    });

    return { total, cash, card, pending };
  }, [movements, start, end]);

  // Para os dados diários, filtrar movimentos que têm pagamentos em cada dia
  const dailyData = React.useMemo(() => {
    return eachDayOfInterval({ start, end }).map(day => {
      let total = 0;
      
      movements.forEach(m => {
        if (m.category !== MovementCategory.OPERATIONAL) return;

        if (m.isMixedPayment) {
          pagamentosDoMovimento(m).forEach(p => {
            const paymentDate = p.paymentDate || m.date;
            if (paymentDate && isSameDay(new Date(paymentDate.split('T')[0]), day)) {
              total += p.value;
            }
          });
        } else {
          // Para pagamentos simples, verificar a data principal
          if (isSameDay(new Date(m.date), day)) {
            total += Number(m.value || 0);
          }
        }
      });

      return { day: format(day, 'd'), value: total };
    });
  }, [movements, start, end]);

  const typeData = [
    { name: 'Multibanco', value: stats.card, color: '#10b981' },
    { name: 'Dinheiro', value: stats.cash, color: '#059669' },
    { name: 'Pendente', value: stats.pending, color: '#fbbf24' }
  ].filter(d => d.value > 0);

  const isComplexEmployee = currentUser?.role === UserRole.EMPLOYEE && currentUser?.userType === UserType.COMPLEX;

  return (
    <div className="max-w-[1400px] mx-auto space-y-8 pb-12">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Relatórios</h1>
          <p className="text-slate-400 mt-1 font-medium">Análise de movimentos e tendências</p>
        </div>
        
        <div className="bg-white border border-slate-100 rounded-2xl flex items-center p-1 shadow-sm w-full sm:w-auto justify-between sm:justify-start">
          <button onClick={() => {
            const newDate = new Date(selectedMonth);
            newDate.setMonth(newDate.getMonth() - 1);
            setSelectedMonth(newDate);
          }} className="p-3 hover:bg-slate-50 rounded-xl text-slate-400 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <span className="px-4 sm:px-8 font-bold text-slate-900 capitalize min-w-[180px] text-center">
            {formatPtMonthYear(selectedMonth)}
          </span>
          <button onClick={() => {
            const newDate = new Date(selectedMonth);
            newDate.setMonth(newDate.getMonth() + 1);
            setSelectedMonth(newDate);
          }} className="p-3 hover:bg-slate-50 rounded-xl text-slate-400 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      {!isComplexEmployee && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[#10b981] p-6 rounded-[2rem] shadow-xl shadow-emerald-50 text-white relative overflow-hidden">
            <TrendingUp size={28} className="opacity-80" />
            <div className="mt-8">
              <p className="text-3xl font-bold leading-tight break-words">{stats.total.toFixed(2)}€</p>
              <p className="text-sm font-medium opacity-80 mt-1">Total do mês</p>
            </div>
          </div>
          <SummaryCard title="Dinheiro" value={stats.cash} icon={<DollarSign className="text-emerald-500" />} />
          <SummaryCard title="Multibanco" value={stats.card} icon={<CreditCard className="text-blue-500" />} />
          <SummaryCard title="Pendente" value={stats.pending} icon={<Clock className="text-amber-500" />} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-8">
            <BarChart className="text-emerald-500" size={24} />
            <h2 className="text-xl font-bold text-slate-800">Vendas Diárias</h2>
          </div>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBar data={dailyData}>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(val) => `${val}€`} />
                <Tooltip 
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="value" fill="#10b981" radius={[6, 6, 0, 0]} />
              </RechartsBar>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Donut Chart */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-8">
            <PieChart className="text-emerald-500" size={24} />
            <h2 className="text-xl font-bold text-slate-800">Distribuição por Tipo</h2>
          </div>
          <div className="h-[250px] relative">
            {typeData.length > 0 ? (
               <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={typeData}
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {typeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-300 italic">Sem dados</div>
            )}
          </div>
          <div className="mt-8 space-y-3">
            {typeData.map(d => (
              <div key={d.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-sm font-semibold text-slate-600">{d.name}</span>
                </div>
                <span className="text-sm font-bold text-slate-900">{d.value.toFixed(2)}€</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon }) {
  return (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between">
      <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-8">
        <p className="text-2xl font-black text-slate-900 leading-tight break-words">{value.toFixed(2)}€</p>
        <p className="text-sm font-medium text-slate-400 mt-1">{title}</p>
      </div>
    </div>
  );
}
